import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "motion/react";
import "./RotatingText.css";

const RotatingText = ({
  texts,
  transition = { type: "spring", damping: 30, stiffness: 400 },
  initial = { y: "100%" },
  animate = { y: 0 },
  exit = { y: "-120%" },
  animatePresenceMode = "wait",
  animatePresenceInitial = false,
  rotationInterval = 2000,
  staggerDuration = 0,
  staggerFrom = "first",
  loop = true,
  auto = true,
  splitLevelClassName = "",
  elementLevelClassName = "",
  mainClassName = "",
  splitBy = "characters",
  onNext,
  ...props
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = useCallback(() => {
    setCurrentIndex((prev) =>
      loop ? (prev + 1) % texts.length : Math.min(prev + 1, texts.length - 1)
    );
    if (onNext) onNext();
  }, [loop, texts.length, onNext]);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(next, rotationInterval);
    return () => clearInterval(id);
  }, [next, rotationInterval, auto]);

  const splitText = (text) => {
    if (splitBy === "characters") return Array.from(text);
    if (splitBy === "words") return text.split(" ");
    if (splitBy === "lines") return text.split("\n");
    return Array.from(text);
  };

  const elements = splitText(texts[currentIndex]);
  const totalElements = elements.length;

  const getStaggerDelay = (index) => {
    if (staggerFrom === "first") return index * staggerDuration;
    if (staggerFrom === "last") return (totalElements - 1 - index) * staggerDuration;
    if (staggerFrom === "center") {
      const center = (totalElements - 1) / 2;
      return Math.abs(index - center) * staggerDuration;
    }
    if (staggerFrom === "random") return Math.random() * staggerDuration * totalElements;
    return index * staggerDuration;
  };

  return (
    <motion.span className={`rotating-text-wrapper ${mainClassName}`} {...props}>
      <AnimatePresence mode={animatePresenceMode} initial={animatePresenceInitial}>
        <motion.span
          key={currentIndex}
          className="rotating-text-word"
          aria-hidden
        >
          {elements.map((el, i) => (
            <span key={i} className={`rotating-text-split ${splitLevelClassName}`}>
              <motion.span
                className={`rotating-text-element ${elementLevelClassName}`}
                initial={initial}
                animate={{
                  ...animate,
                  transition: {
                    ...transition,
                    delay: getStaggerDelay(i),
                  },
                }}
                exit={exit}
                style={{ display: "inline-block" }}
              >
                {el === " " ? "\u00A0" : el}
              </motion.span>
            </span>
          ))}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
};

export default RotatingText;
